/**
 * Beautyrest — Tier Context Manager v6
 *
 * Responsibilities:
 * 1. Collection pages: set br_tier cookie + intercept card clicks
 * 2. PDP: update price DOM from tier prices JSON
 * 3. Combined listings: handle AJAX product switches
 */

window.BR_TIER_VERSION = 6;

(function () {

  // ─── Helpers ──────────────────────────────────────────────────────────────

  function setCookie(name, value) {
    document.cookie = name + '=' + encodeURIComponent(value) + '; path=/; SameSite=Lax';
  }

  function getCookie(name) {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : '';
  }

  function formatMoney(amount) {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'USD',
    }).format(amount);
  }

  function cleanPrice(raw) {
    if (!raw) return 0;
    return parseFloat(String(raw).replace(/[^0-9.]/g, '')) || 0;
  }

  // ─── Tier data ────────────────────────────────────────────────────────────

  function getTier() {
    return getCookie('br_tier') || '';
  }

  function getTierPricesEl() {
    return document.getElementById('br-tier-prices');
  }

  function getTierPrices() {
    const el = getTierPricesEl();
    if (!el) return null;
    try { return JSON.parse(el.textContent); }
    catch (e) { return null; }
  }

  function getPricesForVariant(variantId) {
    const data = getTierPrices();
    if (!data || !variantId) return null;
    return data.variants?.[String(variantId)] || null;
  }

  function getTierPrice(variantId) {
    const tier = getTier();
    if (!tier) return null;
    const prices = getPricesForVariant(variantId);
    if (!prices) return null;
    const raw = tier === 'tier2' ? prices.tier2 : prices.tier1;
    const price = cleanPrice(raw);
    if (!price || price <= 0) return null;
    return { formatted: formatMoney(price), raw: price };
  }

  function getComparePrice(variantId) {
    const prices = getPricesForVariant(variantId);
    if (!prices) return 0;
    return cleanPrice(prices.compare) || 0;
  }

  // ─── Active price container ───────────────────────────────────────────────

  function getActivePriceContainer() {
    const all = document.querySelectorAll('[data-price][data-selected-variant-id]');
    let best = null;
    let bestScore = -1;

    for (const el of all) {
      const rect = el.getBoundingClientRect();
      if (rect.width < 200) continue; // skip small cards
      if (rect.top < 0) continue; // skip off-screen (popup/drawer)
      // Score: wider + higher on page = better
      const score = rect.width - rect.top * 0.1;
      if (score > bestScore) {
        bestScore = score;
        best = el;
      }
    }
    return best || document.querySelector('[data-price]');
  }

  // ─── Price update ─────────────────────────────────────────────────────────

  let _state = {
    variantId: null,
    formatted: null,
    enforceInterval: null,
    enforceUntil: 0,
  };

  function applyPrice(variantId) {
    if (!variantId) return;

    const priceData = getTierPrice(variantId);
    if (!priceData) return;

    const container = getActivePriceContainer();
    if (!container) return;

    // Update sale price
    const salePriceEl = container.querySelector('[data-sale-price]');
    if (salePriceEl) salePriceEl.textContent = priceData.formatted;



    // Track state
    _state.variantId = variantId;
    _state.formatted = priceData.formatted;
  }

  function enforcePrice(variantId, durationMs) {
    if (_state.enforceInterval) {
      clearInterval(_state.enforceInterval);
      _state.enforceInterval = null;
    }
    _state.enforceUntil = Date.now() + durationMs;
    _state.enforceInterval = setInterval(() => {
      if (Date.now() > _state.enforceUntil) {
        clearInterval(_state.enforceInterval);
        _state.enforceInterval = null;
        return;
      }
      applyPrice(variantId);
    }, 50);
  }

  function applyVariantPrice(variantId) {
    if (!variantId) return;
    if (_state.enforceInterval) {
      clearInterval(_state.enforceInterval);
      _state.enforceInterval = null;
    }

    // Apply immediately
    applyPrice(variantId);

    // Enforce for 1.5s to beat Horizon re-renders
    enforcePrice(variantId, 1500);

    // Also apply at key intervals
    [100, 300, 600, 1000].forEach(delay => {
      setTimeout(() => applyPrice(variantId), delay);
    });
  }

  // ─── Combined listing: refresh tier prices JSON via fetch ─────────────────

  function fetchAndUpdateTierPrices(productUrl, variantId) {
    if (!productUrl) return;

    // Fetch the product page and extract the new br-tier-prices JSON
    fetch(productUrl + (productUrl.includes('?') ? '&' : '?') + 'section_id=product-tier-prices-data')
      .then(r => r.text())
      .then(html => {
        // Try to extract br-tier-prices from the fetched HTML
        const match = html.match(/<script[^>]*id="br-tier-prices"[^>]*>([\s\S]*?)<\/script>/);
        if (match) {
          const existing = getTierPricesEl();
          if (existing) {
            existing.textContent = match[1];
            applyVariantPrice(variantId);
          }
        } else {
          // If no new JSON found, still try to apply with current data
          applyVariantPrice(variantId);
        }
      })
      .catch(() => applyVariantPrice(variantId));
  }

  // ─── Collection page: cookie + card click intercept ───────────────────────

  function setTierCookie() {
    const path = window.location.pathname.toLowerCase();
    if (!path.includes('/collections/')) return;
    if (path.includes('tier-2')) setCookie('br_tier', 'tier2');
    else if (path.includes('tier-1')) setCookie('br_tier', 'tier1');
  }

  function interceptProductCardClicks() {
    const path = window.location.pathname.toLowerCase();
    if (!path.includes('/collections/')) return;

    document.addEventListener('click', function (e) {
      const link = e.target.closest('a.product-card__link, a[href*="/products/"]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || !href.includes('/products/')) return;
      if (href.includes('collection=')) return;

      const li = link.closest('[data-collection-handle]');
      const collectionHandle = li?.getAttribute('data-collection-handle') ||
        window.location.pathname.split('/collections/')[1]?.split('/')[0]?.split('?')[0];
      if (!collectionHandle) return;

      e.preventDefault();
      const separator = href.includes('?') ? '&' : '?';
      window.location.href = href + separator + 'collection=' + collectionHandle;
    }, true);
  }

  // ─── PDP: watch for variant changes ───────────────────────────────────────

  function getCurrentVariantId() {
    return new URLSearchParams(window.location.search).get('variant')
      || getActivePriceContainer()?.getAttribute('data-selected-variant-id')
      || document.querySelector('[data-selected-variant-id]')?.getAttribute('data-selected-variant-id')
      || null;
  }

  function watchVariantChange() {
    // Set data-tier on all price containers from cookie
    const tier = getTier();
    const urlCollection = new URLSearchParams(window.location.search).get('collection') || '';
    const tierFromUrl = urlCollection.includes('tier-2') ? 'tier2' : urlCollection.includes('tier-1') ? 'tier1' : '';
    const activeTier = tier || tierFromUrl;

    document.querySelectorAll('[data-price]').forEach(el => {
      if (!el.getAttribute('data-tier') && activeTier) {
        el.setAttribute('data-tier', activeTier === 'tier2' ? 'employee-tier-2' : 'employee-tier-1');
      }
    });

    // variant:update — fired by Horizon after any variant change
    document.addEventListener('variant:update', function (e) {
      const newVariantId = String(e.detail?.resource?.id || '');
      if (!newVariantId) return;

      // For combined listings, the product URL may change
      const productUrl = e.detail?.resource?.url || e.detail?.url || '';
      const isNewProduct = productUrl && !productUrl.includes(window.location.pathname.split('?')[0]);

      if (isNewProduct) {
        // Combined listing switched to a different child product
        // Need to fetch new tier prices
        fetchAndUpdateTierPrices(productUrl.split('?')[0], newVariantId);
      } else {
        applyVariantPrice(newVariantId);
      }
    });

    // Dropdown change (feel/series)
    document.addEventListener('change', function (e) {
      const select = e.target.closest('.variant-option__select');
      if (!select) return;

      const selectedOption = select.options[select.selectedIndex];
      const variantId = selectedOption?.getAttribute('data-variant-id');
      const productUrl = selectedOption?.getAttribute('data-connected-product-url');

      if (productUrl && productUrl !== window.location.pathname) {
        // Combined listing switching product — fetch new tier prices
        setTimeout(() => {
          const newVariantId = variantId || getCurrentVariantId();
          if (newVariantId) fetchAndUpdateTierPrices(productUrl.split('?')[0], newVariantId);
        }, 200);
      } else if (variantId) {
        applyVariantPrice(variantId);
      } else {
        [50, 200, 400].forEach(d => setTimeout(() => {
          const vid = getCurrentVariantId();
          if (vid) applyVariantPrice(vid);
        }, d));
      }
    });

    // Radio button change (size)
    document.addEventListener('change', function (e) {
      const radio = e.target.closest('.variant-option__button-label input[type="radio"]');
      if (!radio) return;
      const variantId = radio.getAttribute('data-variant-id');
      if (variantId) {
        applyVariantPrice(variantId);
      } else {
        setTimeout(() => {
          const vid = getCurrentVariantId();
          if (vid) applyVariantPrice(vid);
        }, 100);
      }
    });

    // MutationObserver — catches Horizon re-renders
    const observer = new MutationObserver(function(mutations) {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (node.matches?.('[data-price]') || node.querySelector?.('[data-price]') ||
              node.matches?.('[data-savings]') || node.querySelector?.('[data-savings]')) {
            const vid = _state.variantId || getCurrentVariantId();
            if (vid) setTimeout(() => applyPrice(vid), 30);
            break;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  // ─── Init ─────────────────────────────────────────────────────────────────

  function init() {
    setTierCookie();
    interceptProductCardClicks();

    if (window.location.pathname.startsWith('/products')) {
      watchVariantChange();

      // Apply at multiple intervals to beat Horizon's render cycle
      [0, 50, 100, 200, 400, 700, 1200, 2000].forEach(delay => {
        setTimeout(() => {
          const variantId = getCurrentVariantId();
          if (variantId) applyVariantPrice(variantId);
        }, delay);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

}());